import React, { useState, useMemo, useRef } from "react";
import axios from "axios";
import { useMutation, useQuery } from "react-query";
import { useSelector } from "react-redux";
import { toast } from "react-toastify";
import Config from "../../Config";
import tw from "tailwind-styled-components";
import { CombineInputGroup, FieldWrappers, InputGroup, Label, SubmitBtn } from "../../Components/Styles/InputStyles";
import { Formik, Form, Field } from "formik";
import * as Yup from "yup";
import { useNavigate } from "react-router-dom";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import { FaRegCalendar } from "react-icons/fa";
import moment from "moment";
// ---------- helpers ----------

const ones = [
    "", "One", "Two", "Three", "Four", "Five", "Six", "Seven", "Eight", "Nine",
    "Ten", "Eleven", "Twelve", "Thirteen", "Fourteen", "Fifteen", "Sixteen",
    "Seventeen", "Eighteen", "Nineteen",
];

const tens = [
    "", "", "Twenty", "Thirty", "Forty", "Fifty", "Sixty", "Seventy", "Eighty", "Ninety",
];

function twoDigits(n) {
    if (n < 20) return ones[n];
    return tens[Math.floor(n / 10)] + (n % 10 ? " " + ones[n % 10] : "");
}

function threeDigits(n) {
    const h = Math.floor(n / 100);
    const rest = n % 100;
    return (h ? ones[h] + " Hundred" + (rest ? " " : "") : "") + (rest ? twoDigits(rest) : "");
}

// Indian numbering system: crore / lakh / thousand / hundred
function numberToWordsINR(num) {
    const n = Math.round(Number(num) || 0);
    if (n === 0) return "Zero Rupees Only";
    let rupees = n;
    const crore = Math.floor(rupees / 10000000);
    rupees %= 10000000;
    const lakh = Math.floor(rupees / 100000);
    rupees %= 100000;
    const thousand = Math.floor(rupees / 1000);
    rupees %= 1000;
    const hundred = rupees;

    const parts = [];
    if (crore) parts.push(threeDigits(crore) + " Crore");
    if (lakh) parts.push(threeDigits(lakh) + " Lakh");
    if (thousand) parts.push(threeDigits(thousand) + " Thousand");
    if (hundred) parts.push(threeDigits(hundred));

    return parts.join(" ").replace(/\s+/g, " ").trim() + " Rupees Only";
}

const fmt = (n) =>
    (Number(n) || 0).toLocaleString("en-IN", {
        maximumFractionDigits: 2,
    });

// ---------- field primitives ----------

function SectionHeading({ index, title }) {
    return (
        <div className="mb-4 flex items-center gap-2 border-l-4 border-[#2b6cb0] bg-[#ebf8ff] px-3 py-2">
            <span className="text-[10px] font-bold tracking-wide text-black">
                {index}
            </span>
            <span className="text-[11px] font-bold uppercase tracking-wide text-black">
                {title}
            </span>
        </div>
    );
}

const getErrorBorder = (touched, error) => {
    return touched && error
        ? "border border-red-500"
        : "border border-gray-300";
};

const isValidDecimal = (value, decimals = 2) => {
    const regex = new RegExp(`^\\d*(\\.\\d{0,${decimals}})?$`);
    return regex.test(value);
};

const fmtInput = (value) => {
    if (!value) return "";
    return new Intl.NumberFormat("en-IN").format(value);
};

// ---------- main form ----------

export default function CommissionReceiptForm() {
    const navigate = useNavigate();
    const user = useSelector((state) => state.UserReducer.user);
    const [calendarOpen, setCalendarOpen] = useState(false);
    const [itemErrors, setItemErrors] = useState({});
    const [items, setItems] = useState([
        {
            description: "",
            rate: "",
            sgst: "",
            cgst: "",
        },
    ]);
    const updateItem = (index, field, value) => {
        const newItems = [...items];
        newItems[index][field] = value;
        setItems(newItems);
    };

    const addItem = () => {
        setItems([
            ...items,
            {
                description: "",
                rate: "",
                sgst: "",
                cgst: "",
            }
        ]);
    };

    const removeItem = (index) => {
        setItems(items.filter((_, i) => i !== index));
    };

    //---Add receipt---------------------------    
    const addReceiptFunction = async (values) => {
        return await axios.post(
            `${Config.apiUrl}/receipt/add`,
            values,
            {
                headers: {
                    authorization: `Bearer ${user.token}`,
                    "Content-Type": "application/json",
                },
            }
        );
    };

    const { mutate: addReceiptMutate, isLoading } = useMutation(
        addReceiptFunction,
        {
            onSuccess: (res) => {
                toast.success(res?.data?.msg || "Receipt added successfully");
                navigate("/invoice");
            },
            onError: (err) => {
                toast.error(err?.response?.data?.msg || "Something went wrong");
            },
        }
    );


    const computed = useMemo(() => {

        const subtotal = items.reduce(
            (sum, item) => sum + Number(item.rate),
            0
        );

        const sgst = items.reduce(
            (sum, item) =>
                sum + (Number(item.rate) * Number(item.sgst)) / 100,
            0
        );

        const cgst = items.reduce(
            (sum, item) =>
                sum + (Number(item.rate) * Number(item.cgst)) / 100,
            0
        );
        const totalTax = sgst + cgst;

        return {
            subtotal,
            sgst,
            cgst,
            totaltaxAmount: totalTax,
            grandTotal: subtotal + totalTax,
        };

    }, [items]);

    const initialValues = {
        ownerName: "",
        receiptDate: moment().format("YYYY-MM-DD"),
        receiptNo: "",
        gstin: "",
        address: "",
        remarks: "",
    };

    const validationSchema = Yup.object({
        ownerName: Yup.string().required("Owner name is required"),
        // receiptDate: Yup.date().required("Receipt date is required"),
        // receiptNo: Yup.string().required("Receipt number is required"),
        address: Yup.string().required("Address is required"),
        // gstin: Yup.string().required("Gst number is required "),
        remarks: Yup.string(),
    });


    const handleSubmit = (values) => {

        const errors = {};

        items.forEach((item, index) => {
            if (!item.description.trim()) {
                errors[`description-${index}`] = "Required";
            }

            if (!item.rate) {
                errors[`rate-${index}`] = "Required";
            }

            if (values.gstin.trim()) {
                if (!item.sgst) {
                    errors[`sgst-${index}`] = "Required";
                }

                if (!item.cgst) {
                    errors[`cgst-${index}`] = "Required";
                }
            }
        });

        if (Object.keys(errors).length) {
            setItemErrors(errors);

            // toast.error("Please fill all required item details.");

            return;
        }

        setItemErrors({});
        const payload = {
            ownerName: values.ownerName,
            receiptDate: values.receiptDate,
            receiptNo: values.receiptNo,
            gstin: values.gstin,
            address: values.address,
            remarks: values.remarks,

            items: items.map((item) => ({
                description: item.description,
                rate: Number(item.rate || 0),
                sgst: Number(item.sgst || 0),
                cgst: Number(item.cgst || 0),
                // amount: Number(item.rate || 0),
            })),

            subtotal: computed.subtotal,
            sgstAmount: computed.sgst,
            cgstAmount: computed.cgst,
            totalTaxAmount: computed.totaltaxAmount,
            grandTotal: computed.grandTotal,
            amountInWords: numberToWordsINR(computed.grandTotal),
        };

        addReceiptMutate(payload);
    };

    return (
        <Formik
            initialValues={initialValues}
            validationSchema={validationSchema}
            onSubmit={handleSubmit}
        >
            {({ values, errors, touched, setFieldValue, setFieldTouched }) => (
                <Form
                    className="min-h-full bg-[#F7F8FA] px-4 py-10">
                    <div className="mx-auto max-w-[1000px]">
                        {/* header */}
                        <div className="mb-8 flex items-start justify-between">
                            <div>
                                 <div className="text-xl  text-black font-semibold tracking-wider">
                                    Generate Invoice
                                </div>
                                {/* <h1 className="mt-1 text-[22px] font-bold text-black">
                                    Generate Invoice
                                </h1> */}
                                <p className="mt-1 text-[13px] text-slate-500">
                                    Create a GST-compliant invoice with customer, billing and tax information.
                                </p>
                            </div>
                        </div>

                        <div className="space-y-5">
                            {/* Section 1 */}
                            <div className="rounded-xl border border-slate-200 bg-white p-5 shadow-sm">
                                <SectionHeading index="1" title="Bill To" />
                                <CombineInputGroup>
                                    <InputGroup>
                                        <Label htmlFor="ownerName">Company/Owner Name *</Label>
                                        <FieldWrappers
                                            className={getErrorBorder(
                                                touched.ownerName,
                                                errors.ownerName
                                            )}
                                        >
                                            <Field
                                                id="ownerName"
                                                name="ownerName"
                                                type="text"
                                                placeholder="Enter name"
                                            />
                                        </FieldWrappers>

                                        {/* {touched.ownerName && errors.ownerName && (
                                            <ErrorText>{errors.ownerName}</ErrorText>
                                        )} */}
                                    </InputGroup>
                                    <InputGroup>
                                        <Label htmlFor="receiptNo">Invoice No.</Label>
                                        <FieldWrappers
                                        >
                                            <Field
                                                id="receiptNo"
                                                name="receiptNo"
                                                placeholder="Enter receipt no."
                                            />
                                        </FieldWrappers>
                                        {/* {touched.ownerName && errors.ownerName && (
                                            <ErrorText>{errors.ownerName}</ErrorText>
                                        )} */}
                                    </InputGroup>


                                </CombineInputGroup>

                                <CombineInputGroup className="mt-2">

                                    <InputGroup>
                                        <Label htmlFor="gstin">Gst No.</Label>

                                        <FieldWrappers
                                        //   className={getErrorBorder(
                                        //     touched.gstin,
                                        //     errors.gstin
                                        // )}
                                        >
                                            <Field
                                                id="gstin"
                                                name="gstin"
                                                placeholder="Enter gst no."
                                                autoComplete="Off"
                                                onChange={(e) => {
                                                const value = e.target.value.toUpperCase();

                                                if (/^[A-Z0-9]*$/.test(value) && value.length <= 15) {
                                                    setFieldValue("gstin", value);
                                                }
                                                }}
                                            />
                                        </FieldWrappers>

                                        {/* {touched.gstin && errors.gstin && (
                                            <ErrorText>{errors.gstin}</ErrorText>
                                        )} */}
                                    </InputGroup>

                                    <InputGroup>
                                        <Label htmlFor="receiptDate">Invoice Date *</Label>

                                        <FieldWrappers className="relative h-11">
                                            <DatePicker
                                                selected={
                                                    values.receiptDate
                                                        ? new Date(values.receiptDate)
                                                        : null
                                                }
                                                onChange={(date) => {
                                                    setFieldValue(
                                                        "receiptDate",
                                                        moment(date).format("YYYY-MM-DD")
                                                    );
                                                    setCalendarOpen(false);
                                                }}
                                                open={calendarOpen}
                                                onInputClick={() => setCalendarOpen(true)}
                                                onClickOutside={() => setCalendarOpen(false)}
                                                onSelect={() => setCalendarOpen(false)}
                                                dateFormat="yyyy-MM-dd"
                                                placeholderText="Select receipt date"
                                                className="w-full cursor-pointer bg-transparent outline-none"
                                                showIcon
                                                icon={<FaRegCalendar className="text-gray-400 text-xs mt-0.5" />}
                                                showPopperArrow={false}
                                                wrapperClassName="w-full"
                                                portalId="root-portal"
                                                popperClassName="react-datepicker-popper"
                                                onFocus={(e) => e.target.blur()}
                                            />
                                        </FieldWrappers>

                                        {touched.receiptDate && errors.receiptDate && (
                                            <ErrorText>{errors.receiptDate}</ErrorText>
                                        )}
                                    </InputGroup>
                                </CombineInputGroup>

                                <div className="mt-2">
                                    <InputGroup>
                                        <Label htmlFor="address">Address *</Label>

                                        <Field
                                            as={Textarea}
                                            id="address"
                                            name="address"
                                            rows={3}
                                            placeholder="Enter address"
                                        />

                                        {touched.address && errors.address && (
                                            <ErrorText>{errors.address}</ErrorText>
                                        )}
                                    </InputGroup>
                                </div>
                            </div>

                            {/* Section 2 */}
                            <div className="rounded-xl border border-slate-200 bg-white p-5 shadow-sm">
                                <SectionHeading index="2" title="Items" />

                                <table className="w-full text-sm border">
                                    <thead className="bg-[#26A69A] text-white">
                                        <tr>
                                            <th className="p-2 text-left">Item Description</th>
                                            <th>Rate</th>
                                            <th>SGST %</th>
                                            <th>CGST %</th>
                                            <th className="w-40 text-right">Amount</th>
                                            <th></th>
                                        </tr>
                                    </thead>

                                    <tbody className="divide-y divide-slate-200">
                                        {items.map((item, index) => {
                                            // const amount = Number(item.rate);
                                            const rate = Number(item.rate || 0);

                                            return (
                                                <tr key={index} className="bg-white hover:bg-slate-50">

                                                    {/* Description */}
                                                    <td className="p-3 w-[45%]">
                                                        <FieldWrappers
                                                            className={
                                                                itemErrors[`description-${index}`]
                                                                    ? "border border-red-500"
                                                                    : ""
                                                            }
                                                        >
                                                            <input
                                                                type="text"
                                                                className="w-full bg-transparent"
                                                                placeholder="Item Description"
                                                                value={item.description}
                                                                onChange={(e) => {
                                                                    updateItem(index, "description", e.target.value);

                                                                    if (e.target.value.trim()) {
                                                                        setItemErrors((prev) => {
                                                                            const copy = { ...prev };
                                                                            delete copy[`description-${index}`];
                                                                            return copy;
                                                                        });
                                                                    }
                                                                }}
                                                            />
                                                        </FieldWrappers>
                                                    </td>

                                                    {/* Rate */}
                                                    <td className="p-3 w-[15%]">
                                                        <FieldWrappers
                                                            className={
                                                                itemErrors[`rate-${index}`]
                                                                    ? "border border-red-500"
                                                                    : ""
                                                            }
                                                        >
                                                            <input
                                                                type="text"
                                                                inputMode="numeric"
                                                                className="w-full bg-transparent"
                                                                placeholder="Amount..."
                                                                value={fmtInput(item.rate)}
                                                                onChange={(e) => {
                                                                    const rawValue = e.target.value.replace(/,/g, "");

                                                                    if (rawValue === "" || /^\d+$/.test(rawValue)) {
                                                                        const amount = Number(rawValue);

                                                                        if (amount <= 1000000 || rawValue === "") {
                                                                            updateItem(index, "rate", rawValue);

                                                                            if (rawValue) {
                                                                                setItemErrors((prev) => {
                                                                                    const copy = { ...prev };
                                                                                    delete copy[`rate-${index}`];
                                                                                    return copy;
                                                                                });
                                                                            }
                                                                        } else {
                                                                            toast.error("Amount cannot be greater than ₹10,00,000");
                                                                        }
                                                                    }
                                                                }}
                                                            />
                                                        </FieldWrappers>
                                                    </td>

                                                    {/* SGST */}
                                                    <td className="p-3 w-[12%]">
                                                        <FieldWrappers
                                                            className={`
                                                                ${itemErrors[`sgst-${index}`] ? "border border-red-500" : ""}
                                                                ${!values.gstin.trim() ? "bg-gray-100 opacity-60 cursor-not-allowed" : ""}
                                                            `}
                                                        >
                                                            <input
                                                                type="text"
                                                                inputMode="decimal"
                                                                className={`w-full bg-transparent ${!values.gstin.trim() ? "cursor-not-allowed text-gray-400" : ""
                                                                    }`}
                                                                placeholder="SGST.."
                                                                value={item.sgst}
                                                                disabled={!values.gstin.trim()}
                                                                onChange={(e) => {
                                                                    const rawValue = e.target.value.replace(/,/g, "");

                                                                    if (
                                                                        rawValue === "" ||
                                                                        (isValidDecimal(rawValue, 4) &&
                                                                            Number(rawValue) <= 100)
                                                                    ) {
                                                                        updateItem(index, "sgst", rawValue);

                                                                        setItemErrors((prev) => {
                                                                            const copy = { ...prev };
                                                                            delete copy[`sgst-${index}`];
                                                                            return copy;
                                                                        });
                                                                    }
                                                                }}
                                                            />
                                                        </FieldWrappers>
                                                    </td>

                                                    {/* CGST */}
                                                    <td className="p-3 w-[12%]">
                                                        <FieldWrappers
                                                            className={`
                                                                ${itemErrors[`cgst-${index}`] ? "border border-red-500" : ""}
                                                                ${!values.gstin.trim() ? "bg-gray-100 opacity-60 cursor-not-allowed" : ""}
                                                            `}
                                                        >
                                                            <input
                                                                type="text"
                                                                inputMode="decimal"
                                                                className={`w-full bg-transparent ${!values.gstin.trim() ? "cursor-not-allowed text-gray-400" : ""
                                                                    }`}
                                                                placeholder="CGST.."
                                                                value={item.cgst}
                                                                disabled={!values.gstin.trim()}
                                                                onChange={(e) => {
                                                                    const rawValue = e.target.value.replace(/,/g, "");

                                                                    if (
                                                                        rawValue === "" ||
                                                                        (isValidDecimal(rawValue, 4) &&
                                                                            Number(rawValue) <= 100)
                                                                    ) {
                                                                        updateItem(index, "cgst", rawValue);

                                                                        setItemErrors((prev) => {
                                                                            const copy = { ...prev };
                                                                            delete copy[`cgst-${index}`];
                                                                            return copy;
                                                                        });
                                                                    }
                                                                }}
                                                            />
                                                        </FieldWrappers>
                                                    </td>

                                                    {/* Amount */}
                                                    <td className="w-40 text-right whitespace-nowrap font-semibold text-[#1A365D]">
                                                        ₹ {rate === 0 ? "" : fmt(rate)}
                                                    </td>

                                                    {/* Delete */}
                                                    <td className="w-[5%] text-center">
                                                        {items.length > 1 && (
                                                            <button
                                                                type="button"
                                                                onClick={() => removeItem(index)}
                                                                className="flex h-8 w-8 items-center justify-center rounded-full text-red-500 hover:bg-red-100"
                                                            >
                                                                ✕
                                                            </button>
                                                        )}
                                                    </td>
                                                </tr>
                                            );
                                        })}
                                    </tbody>
                                </table>

                                <button
                                    type="button"
                                    onClick={addItem}
                                    className="mt-2 text-primary-color hover:underline font-medium text-[15px]"
                                >
                                    + Add Line Item
                                </button>
                                <div className="mt-6 ml-auto w-80 space-y-3">

                                    <div className="flex justify-between">
                                        <span>Sub Total</span>
                                        <span>₹ {fmt(computed.subtotal)}</span>
                                    </div>

                                    <div className="flex justify-between">
                                        <span>SGST</span>
                                        <span>₹ {fmt(computed.sgst)}</span>
                                    </div>

                                    <div className="flex justify-between">
                                        <span>CGST</span>
                                        <span>₹ {fmt(computed.cgst)}</span>
                                    </div>

                                    <div className="flex justify-between text-lg font-bold border-t pt-3">
                                        <span>Total</span>
                                        <span>₹ {fmt(computed.grandTotal)}</span>
                                    </div>

                                </div>
                            </div>

                            {/* Section 3 */}
                            <div className="rounded-xl border border-slate-200 bg-white p-5 shadow-sm">
                                <SectionHeading index="3" title="Additional Details" />
                                <InputGroup>
                                    <Label htmlFor="remarks">Remarks</Label>
                                    <FieldWrapper>
                                        <Field
                                            as={Textarea}
                                            id="remarks"
                                            name="remarks"
                                            rows={3}
                                            maxLength={500}
                                            placeholder="Write your remarks here..."
                                        />
                                    </FieldWrapper>
                                </InputGroup>
                            </div>
                        </div>

                        {/* footer action */}
                        <div className="mt-8 flex justify-end">
                            {/* <button
                                type="submit"
                                // disabled={isLoading}
                                className="rounded-lg bg-[#1a365d] px-6 py-2.5 text-sm font-semibold text-white hover:bg-[#15294a] disabled:opacity-60"
                            >
                                {isLoading ? "Submitting..." : "Submit Receipt"}
                            </button> */}

                            <SubmitBtn type="submit" disabled={isLoading}
                                onClick={() => {
                                    const errors = {};

                                    items.forEach((item, index) => {
                                        if (!item.description.trim()) {
                                            errors[`description-${index}`] = "Required";
                                        }

                                        if (!item.rate) {
                                            errors[`rate-${index}`] = "Required";
                                        }

                                        if (values.gstin.trim()) {
                                            if (!item.sgst) errors[`sgst-${index}`] = "Required";
                                            if (!item.cgst) errors[`cgst-${index}`] = "Required";
                                        }
                                    });

                                    setItemErrors(errors);

                                    document.querySelector("form").requestSubmit();
                                }}

                            >
                                {isLoading ? "Generating..." : "Generate Invoice"}
                            </SubmitBtn>
                        </div>
                    </div>
                </Form>
            )}
        </Formik>
    );
}

const Textarea = tw.textarea`min-h-30 border border-gray-300 rounded pl-2 py-2 focus:ring-1 focus:ring-sky-600  focus:border-sky-600 focus:outline-none duration-200 w-full h-30 resize-y  break-words overflow-hidden`;
const FieldWrapper = tw.div`w-full`;
const ErrorText = tw.div`text-red-500 text-sm ml-0.5`;
